// Estado del editor de marca de agua — mismo esquema que useCropState: el HOST
// (GeneratedImagesInline / GeneratedImagesHistory) crea el estado con este hook
// y ubica <WatermarkEditorCanvas> y <WatermarkEditorControls> dentro de SU
// layout, igual que hace Recortar. Así el editor hereda el shell de cada
// superficie (sidebar y top bar en desktop, bg y navegación de mobile) en vez
// de taparlo con un overlay propio.
//
// Capas: por defecto arranca SOLO el logo (o solo el texto si la marca no tiene
// logo); cada pestaña permite agregar la capa que falta o eliminar la que está.
// Guardar sin ninguna capa persiste la config desactivada (equivale a no tener
// marca de agua: el selector de descarga no aparece).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useBrandColors } from '../../pages/edicionGrafica/lib/brandColors';
import { loadImageRatio } from '../../pages/edicionGrafica/lib/factory';
import { useScene } from '../../pages/edicionGrafica/scene/useScene';
import type { ImageLayer, TextLayer } from '../../pages/edicionGrafica/scene/types';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { postRemoveBackground } from '../../lib/watermarkBackend';
import { buildWatermarkLayers, resolveLogoSrc, tintWhite } from '../../utils/watermark';
import {
  defaultWatermarkConfig,
  fetchWatermarkConfig,
  saveWatermarkConfig,
  type WatermarkConfig,
  type WatermarkLogoMode,
} from '../../utils/watermarkConfig';

export type WatermarkTab = 'logo' | 'texto';

export function useWatermarkEditor(opts: {
  /** true mientras el editor está abierto en la superficie. */
  active: boolean;
  /** Imagen de preview (signed URL). Solo de muestra: no se modifica. */
  imageUrl: string | null;
  /** Cierra el editor en el host (cancelar, guardar o desactivar). */
  onClose: () => void;
}) {
  const { active, imageUrl, onClose } = opts;
  const api = useScene();
  const { activeBrand, loading } = useBrandProfile();
  const brandColors = useBrandColors(activeBrand?.id ?? null);

  const [config, setConfig] = useState<WatermarkConfig | null>(null);
  const [hadConfig, setHadConfig] = useState(false);
  const [ready, setReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const initedRef = useRef(false);

  const brandLogoUrl = activeBrand?.logo_url ?? null;
  const logoLayerExists = api.scene.layers.some((l) => l.id === 'wm-logo');
  const textLayerExists = api.scene.layers.some((l) => l.id === 'wm-text');

  // ── Init al abrir / reset al cerrar ──
  useEffect(() => {
    if (!active || !imageUrl) {
      initedRef.current = false;
      setReady(false);
      setConfig(null);
      setShowSaved(false);
      setSaveError(null);
      setLogoError(null);
      return;
    }
    if (loading || !activeBrand || initedRef.current) return;
    initedRef.current = true;
    let cancelled = false;
    (async () => {
      const fetched = await fetchWatermarkConfig(activeBrand.id);
      let cfg: WatermarkConfig;
      if (fetched) {
        cfg = fetched;
      } else {
        // Primera vez: UNA sola capa — el logo si la marca tiene, si no el texto.
        const d = defaultWatermarkConfig(activeBrand.name);
        const hasLogo = !!activeBrand.logo_url;
        cfg = {
          ...d,
          logo: { ...d.logo, visible: hasLogo },
          text: { ...d.text, visible: !hasLogo },
        };
      }
      // El logo de la marca cambió desde que se procesó el "sin fondo": la
      // versión transparente guardada es de OTRO logo — se invalida.
      if (cfg.logo.noBgUrl && cfg.logo.noBgSourceUrl !== (activeBrand.logo_url ?? null)) {
        cfg = {
          ...cfg,
          logo: { ...cfg.logo, noBgUrl: null, noBgSourceUrl: null, mode: 'original' },
        };
      }
      const ratio = await loadImageRatio(imageUrl);
      const logoSrc = await resolveLogoSrc(cfg, activeBrand.logo_url ?? null).catch(
        () => activeBrand.logo_url ?? null,
      );
      const logoNaturalRatio = logoSrc ? await loadImageRatio(logoSrc) : 1;
      if (cancelled) return;
      const base: ImageLayer = {
        id: 'base',
        type: 'image',
        src: imageUrl,
        xPct: 50,
        yPct: 50,
        wPct: 100,
        hPct: 100,
        radiusPct: 0,
        locked: true,
      };
      const wmLayers = buildWatermarkLayers(cfg, { canvasRatio: ratio, logoSrc, logoNaturalRatio });
      api.init({
        bg: '#FFFFFF',
        ratio,
        selectedId: wmLayers.some((l) => l.id === 'wm-logo')
          ? 'wm-logo'
          : wmLayers.some((l) => l.id === 'wm-text')
            ? 'wm-text'
            : null,
        layers: [base, ...wmLayers],
      });
      setConfig(cfg);
      setHadConfig(!!fetched);
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // Deps PRIMITIVAS a propósito: `activeBrand` (objeto derivado con .find) y
    // `api` (objeto literal del hook) cambian de identidad en CADA render — con
    // ellos en deps, este efecto corría por render y realimentaba un bucle de
    // updates (visto el 28/08: "Maximum update depth exceeded" al abrir el
    // editor). `api.init` sí es estable (useCallback []).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, imageUrl, loading, activeBrand?.id, activeBrand?.name, activeBrand?.logo_url, api.init]);

  // ── Tab activo ⟷ selección del lienzo (sincronizados) ──
  const activeTab: WatermarkTab = api.scene.selectedId === 'wm-text' ? 'texto' : 'logo';
  const selectTab = useCallback(
    (tab: WatermarkTab) => api.select(tab === 'logo' ? 'wm-logo' : 'wm-text'),
    [api],
  );

  // ── Agregar / eliminar capas (ADD/REMOVE_LAYER hacen checkpoint solos) ──
  const addLogoLayer = useCallback(async () => {
    if (!config || !brandLogoUrl) return;
    const logoSrc = await resolveLogoSrc(config, brandLogoUrl).catch(() => brandLogoUrl);
    const logoNaturalRatio = logoSrc ? await loadImageRatio(logoSrc) : 1;
    const [layer] = buildWatermarkLayers(
      {
        ...config,
        logo: { ...config.logo, visible: true },
        text: { ...config.text, visible: false },
      },
      { canvasRatio: api.scene.ratio, logoSrc, logoNaturalRatio },
    );
    if (!layer) return;
    api.addLayer(layer);
    api.select('wm-logo');
  }, [config, brandLogoUrl, api]);

  const addTextLayer = useCallback(() => {
    if (!config || !activeBrand) return;
    const [layer] = buildWatermarkLayers(
      {
        ...config,
        logo: { ...config.logo, visible: false },
        text: {
          ...config.text,
          visible: true,
          text: config.text.text.trim() || activeBrand.name || 'Mi marca',
        },
      },
      { canvasRatio: api.scene.ratio, logoSrc: null, logoNaturalRatio: 1 },
    );
    if (!layer) return;
    api.addLayer(layer);
    api.select('wm-text');
  }, [config, activeBrand, api]);

  const removeLayer = useCallback(
    (tab: WatermarkTab) => {
      const id = tab === 'logo' ? 'wm-logo' : 'wm-text';
      const otherId = tab === 'logo' ? 'wm-text' : 'wm-logo';
      const hasOther = api.scene.layers.some((l) => l.id === otherId);
      api.removeLayer(id);
      api.select(hasOther ? otherId : null);
    },
    [api],
  );

  // ── Modo del logo (original / sin fondo / blanco) ──
  const setLogoMode = useCallback(
    async (mode: WatermarkLogoMode) => {
      if (!config || !brandLogoUrl) return;
      setLogoError(null);
      try {
        let cfg = config;
        if (mode !== 'original' && !cfg.logo.noBgUrl) {
          setProcessing(true);
          const noBgUrl = await postRemoveBackground(brandLogoUrl);
          cfg = { ...cfg, logo: { ...cfg.logo, noBgUrl, noBgSourceUrl: brandLogoUrl } };
        }
        cfg = { ...cfg, logo: { ...cfg.logo, mode } };
        const src =
          mode === 'original'
            ? brandLogoUrl
            : mode === 'no-bg'
              ? cfg.logo.noBgUrl!
              : await tintWhite(cfg.logo.noBgUrl!);
        setConfig(cfg);
        api.checkpoint();
        api.updateLayer('wm-logo', { src });
      } catch (err) {
        setLogoError(err instanceof Error ? err.message : 'No se pudo procesar el logo.');
      } finally {
        setProcessing(false);
      }
    },
    [config, brandLogoUrl, api],
  );

  // ── Guardar / desactivar ──
  const buildConfigFromScene = useCallback((): WatermarkConfig | null => {
    if (!config) return null;
    const logo = api.scene.layers.find((l): l is ImageLayer => l.id === 'wm-logo');
    const text = api.scene.layers.find((l): l is TextLayer => l.id === 'wm-text');
    return {
      ...config,
      version: 1,
      logo: {
        ...config.logo,
        visible: !!logo,
        ...(logo
          ? {
              xPct: logo.xPct,
              yPct: logo.yPct,
              wPct: logo.wPct,
              rotation: logo.rotation ?? 0,
              opacity: logo.opacity ?? 1,
            }
          : {}),
      },
      text: {
        ...config.text,
        visible: !!text,
        ...(text
          ? {
              text: text.text,
              xPct: text.xPct,
              yPct: text.yPct,
              wPct: text.wPct,
              rotation: text.rotation ?? 0,
              opacity: text.opacity ?? 1,
              fontFamily: text.fontFamily,
              fontSizePct: text.fontSizePct,
              fontWeight: text.fontWeight,
              fontStyle: text.fontStyle,
              color: text.color,
            }
          : {}),
      },
    };
  }, [api.scene.layers, config]);

  const handleSave = useCallback(async () => {
    if (!activeBrand || saving) return;
    const next = buildConfigFromScene();
    if (!next) return;
    setSaving(true);
    setSaveError(null);
    // Sin logo ni texto no hay marca que aplicar: se guarda desactivada (el
    // selector de descarga no aparece) y se cierra sin el modal de éxito.
    if (!next.logo.visible && !next.text.visible) {
      await saveWatermarkConfig(activeBrand.id, { ...next, enabled: false });
      setSaving(false);
      onClose();
      return;
    }
    const res = await saveWatermarkConfig(activeBrand.id, { ...next, enabled: true });
    setSaving(false);
    if (!res.ok) {
      setSaveError(res.error ?? 'No se pudo guardar. Probá de nuevo.');
      return;
    }
    setShowSaved(true);
  }, [activeBrand, saving, buildConfigFromScene, onClose]);

  const handleDisable = useCallback(async () => {
    if (!activeBrand || saving) return;
    const next = buildConfigFromScene();
    if (!next) return;
    setSaving(true);
    await saveWatermarkConfig(activeBrand.id, { ...next, enabled: false });
    setSaving(false);
    onClose();
  }, [activeBrand, saving, buildConfigFromScene, onClose]);

  const closeSaved = useCallback(() => {
    setShowSaved(false);
    onClose();
  }, [onClose]);

  return {
    api,
    brandColors,
    config,
    ready,
    hadConfig,
    processing,
    logoError,
    saving,
    saveError,
    showSaved,
    brandLogoUrl,
    logoLayerExists,
    textLayerExists,
    activeTab,
    selectTab,
    addLogoLayer,
    addTextLayer,
    removeLayer,
    setLogoMode,
    handleSave,
    handleDisable,
    closeSaved,
    onClose,
  };
}

export type WatermarkEditorState = ReturnType<typeof useWatermarkEditor>;
